import { getSocket } from '@/lib/socket';
import { toast } from '@/lib/toast';
import { useDataStore } from '@/stores/dataStore';
import i18n from '@/i18n';

/**
 * Subscribe this socket to a channel's live-event room, VERIFIED via ack.
 *
 * The previous fire-and-forget `socket.emit('channel:join')` had a silent
 * failure mode: if the server refused (permission change, deleted channel,
 * soft-deleted guild) or the handler died, the client still believed it was
 * in the room — new messages/reactions/typing for the open channel simply
 * stopped arriving, with the UI looking "quiet" instead of broken. The ack +
 * timeout turns that into one retry and, if that also fails, a visible
 * warning so the user knows live updates are degraded.
 */
const ACK_TIMEOUT_MS = 8_000;
const RETRY_DELAY_MS = 2_000;

export function joinChannelRoom(channelId: string): void {
  const socket = getSocket();
  const attempt = (retriesLeft: number) => {
    socket
      .timeout(ACK_TIMEOUT_MS)
      .emit(
        'channel:join',
        { channelId },
        (err: Error | null, result?: { ok?: boolean; error?: string }) => {
          if (!err && result?.ok) return;
          // The user may have moved on while we waited — a failure for a
          // channel that's no longer open is not worth a retry or a warning.
          if (useDataStore.getState().currentChannelId !== channelId) return;
          if (retriesLeft > 0) {
            setTimeout(() => attempt(retriesLeft - 1), RETRY_DELAY_MS);
            return;
          }
          toast.warning(i18n.t('messages.roomJoinFailed'), { duration: 6000 });
        },
      );
  };
  attempt(1);
}
